export interface ChartAiFollowupItem {
  q: string;
  a: string;
}

export interface BuildChartAiUserMessageParams {
  dataText: string;
  question: string;
  extraQuestion?: string;
  priorReport?: string;
  followupHistory?: ChartAiFollowupItem[];
  shenYongSuggestion?: string;
}

function pickChartFollowupKeyLines(dataText: string, limit = 24): string[] {
  return String(dataText || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => /事项|旬首|值符|值使|马星|空亡|四柱|五柱|年柱|月柱|日柱|时柱|刻柱|主体干|事项干|天盘干落宫索引|转宫提醒|真假空亡|打开引干|日时同宫|伏吟|宫：|击刑|门迫|入墓/.test(line))
    .slice(0, limit);
}

export function buildChartAiUserMessage(params: BuildChartAiUserMessageParams): string {
  const mainQuestion = String(params.question || '').trim() || '请做综合判断';
  const extraQuestion = String(params.extraQuestion || '').trim();
  const priorReport = String(params.priorReport || '').trim();
  const history = Array.isArray(params.followupHistory) ? params.followupHistory : [];
  const shenYongSuggestion = String(params.shenYongSuggestion || '').trim();

  return [
    '【盘面/素材数据】',
    String(params.dataText || '').trim() || '无',
    '',
    '【用户问题】',
    extraQuestion ? `${mainQuestion}\n补充提问：${extraQuestion}` : mainQuestion,
    shenYongSuggestion ? `\n\n【取用神建议】\n${shenYongSuggestion}` : '',
    priorReport ? `\n【上一轮解析】\n${priorReport}` : '',
    history.length > 0
      ? `\n【历史追问】\n${history.map((item, idx) => `${idx + 1}. 问：${item.q}\n答：${item.a}`).join('\n')}`
      : '',
    '\n【输出要求】',
    '1. 内部推演区保留必要结构，但标题层级要收敛：只保留“取用神定宫”这个主标题，严禁再输出“取定用神宫”“核心用神定位”；“四害溯源与引干分析”下如需展开，只允许使用更低一级的“真假入墓”“反断判定”小标题，严禁再输出“真假入墓空亡与反断判定”。',
    '2. 宫位专业术语如“坤二宫”“乾六宫”“艮八宫”等可直接加粗，方便用户扫读。',
    '3. “大师直解”里每个主段落先把白话结论讲完整，再在括号里补专业依据；像反断局、真假入墓、四害、转宫、打开引干这类专门规则尽量都放进括号。',
    '4. 即使前端切到“直断模式”会隐藏括号，正文主句也必须自成一体，而且括号外只保留通俗语言，不要把术语关键字裸露在括号外。',
    '5. 引用任何宫位时，必须逐一核对该宫真实门、星、神、天盘干、地盘干；不得把别宫符号、原宫与转宫符号串写到当前宫。正文里要直接写出“天盘某干代表什么、地盘某干代表什么”，不能只把地盘干当标签，也不能只讲星门神。',
    '6. 若括号里引用了休门、天冲、值符、白虎、乙奇、庚、击刑、入墓等象意，括号外也要同步翻译成现实话，把它对应的机会、阻力、人物、资源、节奏或结果讲清楚，不能把关键信息只藏在括号里。',
    '7. 括号里的依据保持短而准，点到宫位、门星神干、四害、生克或转宫即可，不必把每个符号逐个长篇解释。',
    '8. “结论先行”“断局主线/象义优先”这类段落要把因果讲透，不要只给几句结论。',
    '9. 禁止使用“被扣在碗底下的蛐蛐”这类怪异比喻，优先用现实阻滞、卡住、施展不开等表达。',
  ].filter(Boolean).join('\n');
}

export function buildChartAiFollowupMessage(params: BuildChartAiUserMessageParams): string {
  const mainQuestion = String(params.question || '').trim() || '请做综合判断';
  const extraQuestion = String(params.extraQuestion || '').trim();
  const priorReport = String(params.priorReport || '').trim();
  const history = Array.isArray(params.followupHistory) ? params.followupHistory : [];
  const keyLines = pickChartFollowupKeyLines(params.dataText, 24);
  const recentHistory = history;
  const useChatStyle = history.length >= 1;

  if (useChatStyle) {
    return [
      '【追问上下文】',
      `原问题：${mainQuestion}`,
      `本次追问：${extraQuestion || '请直接接着上一轮说明。'}`,
      keyLines.length > 0 ? `\n【盘面关键摘录】\n${keyLines.join('\n')}` : '',
      priorReport ? `\n【首轮核心结论】\n${priorReport}` : '',
      recentHistory.length > 0
        ? `\n【最近对话】\n${recentHistory.map((item, idx) => `${idx + 1}. 问：${item.q}\n答：${item.a}`).join('\n')}`
        : '',
      '\n请像正常聊天一样直接回答这次追问：先直接回答，再补最关键的 2 到 3 条盘面依据。',
      '不要重写整份报告，不要写“内部推演 / 大师直解”，也不要输出“奇门运筹”“环境调整/环境规划”“应期判断”这些栏目名；如果用户问到行动、环境或时间，就直接用普通聊天口吻回答。',
      '若这次追问涉及某个宫位或某组符号，仍要把门、星、神、天盘干、地盘干一起看，尤其要把天盘干、地盘干各自对应的现实含义直接讲在正文里；若某个干命中四害，也要单独说明它受害后的现实表现。',
      '优先承接上文语境，把这次问题说透即可，不要人为压缩篇幅；该讲完的判断、依据和建议要完整讲清。',
    ].filter(Boolean).join('\n');
  }

  return [
    '【追问任务】',
    `原问题：${mainQuestion}`,
    `本次追问：${extraQuestion || '请补充关键判断与建议。'}`,
    keyLines.length > 0 ? `\n【盘面关键摘录】\n${keyLines.join('\n')}` : '',
    priorReport ? `\n【上一轮结论】\n${priorReport}` : '',
    recentHistory.length > 0
      ? `\n【最近追问记录】\n${recentHistory.map((item, idx) => `${idx + 1}. 问：${item.q}\n答：${item.a}`).join('\n')}`
      : '',
    '\n请直接回答这次追问，不必重抄整盘，但要把这次问题讲透。',
    '默认只保留：直接结论、关键依据、必要建议。',
    '不要输出“奇门运筹”“环境调整/环境规划”“应期判断”这些栏目名；如果用户问到行动、环境或时间，也直接用普通对话回答，不要单独起段落栏目。',
    '若涉及术语，主句先讲白话，再在括号里补 1 到 2 句专业解释；像四害、转宫、反断局这类专门规则优先放进括号，括号解释不要只写术语名，但也不必把每个符号逐个展开讲课；若括号里提了具体象意，主句也要把对应的现实含义一并讲清。若提到某宫，天盘干和地盘干各自的现实含义也必须直接讲出来。',
  ].filter(Boolean).join('\n');
}

export function buildChartAiKnowledgeQuery(dataText: string, question: string): string {
  const q = String(question || '').trim();
  const lines = String(dataText || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const keyLines = lines
    .filter((line) => /旬首|值符|值使|马星|空亡|宫：|击刑|门迫|入墓/.test(line))
    .slice(0, 8);
  const fallback = lines.slice(0, 4);
  return [q || '综合判断', ...(keyLines.length > 0 ? keyLines : fallback)].join('\n');
}
